import React, { useState, useEffect } from 'react';
import api from '../services/api';
import './AdminPagos.css';

const AdminPagos = () => {
    const [pagos, setPagos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filtroEstado, setFiltroEstado] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    
    // Modal para ver comprobante
    const [pagoSeleccionado, setPagoSeleccionado] = useState(null);

    const fetchPagos = async () => {
        try {
            setLoading(true);
            const res = await api.get('/admin/pagos');
            setPagos(res.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Error al cargar pagos');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPagos();
    }, []);

    const handleValidar = async (id, accion) => {
        const confirmMsg = accion === 'aprobar' 
            ? '¿Estás seguro que deseas aprobar este pago y confirmar el turno?' 
            : '¿Rechazar este comprobante de pago?';
            
        if (!window.confirm(confirmMsg)) return;

        try {
            await api.put(`/admin/pagos/${id}/validar`, { accion });
            setPagoSeleccionado(null); // Cerrar modal si estaba abierto
            fetchPagos(); // Recargar lista
        } catch (err) {
            alert('Error al procesar el pago');
            console.error(err);
        }
    };

    const formatearMoneda = (monto) =>
        new Intl.NumberFormat('es-AR', {
            style: 'currency',
            currency: 'ARS',
            minimumFractionDigits: 0,
        }).format(monto || 0);

    const formatearFecha = (fecha) =>
        new Date(fecha).toLocaleString('es-AR', {
            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
        });

    const pagosFiltrados = pagos.filter((p) => {
        const matchEstado = filtroEstado === 'all' || p.estado === filtroEstado;
        const term = searchTerm.toLowerCase();
        const matchSearch =
            !searchTerm ||
            (p.paciente?.nombre || '').toLowerCase().includes(term) ||
            (p.paciente?.email || '').toLowerCase().includes(term) ||
            (p.mpPaymentId || '').toString().includes(term);
        return matchEstado && matchSearch;
    });

    const totalRecaudado = pagosFiltrados
        .filter((p) => p.estado === 'approved')
        .reduce((acc, p) => acc + (p.monto || 0), 0);

    return (
        <div className="admin-pagos">
            <div className="admin-page-header" style={{ marginBottom: '1.5rem' }}>
                <h1>💰 Historial de Pagos</h1>
                <p className="admin-page-subtitle">Seguimiento de señas y pagos recibidos</p>
            </div>

            <div className="pagos-filtros">
                <div className="filter-group">
                    <button className={`filter-btn ${filtroEstado === 'all' ? 'active' : ''}`} onClick={() => setFiltroEstado('all')}>Todos</button>
                    <button className={`filter-btn ${filtroEstado === 'approved' ? 'active' : ''}`} onClick={() => setFiltroEstado('approved')}>✅ Aprobados</button>
                    <button className={`filter-btn ${filtroEstado === 'en_revision' ? 'active' : ''}`} onClick={() => setFiltroEstado('en_revision')}>⏳ En Revisión</button>
                    <button className={`filter-btn ${filtroEstado === 'rejected' ? 'active' : ''}`} onClick={() => setFiltroEstado('rejected')}>❌ Rechazados</button>
                </div>
                <input
                    type="text"
                    className="form-control search-input"
                    placeholder="Buscar por paciente o ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            <div className="pagos-resumen">
                <span>Total recaudado: <strong>{formatearMoneda(totalRecaudado)}</strong></span>
                <span>{pagosFiltrados.length} pagos</span>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}

            {loading ? (
                <div className="pagos-loading">Cargando pagos...</div>
            ) : pagosFiltrados.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-state-icon">💸</div>
                    <div className="empty-state-title">No hay pagos</div>
                    <div className="empty-state-text">No se encontraron pagos con los filtros aplicados</div>
                </div>
            ) : (
                <div className="pagos-tabla">
                    <div className="tabla-header" style={{ gridTemplateColumns: '1fr 1.5fr 1fr 1fr 1fr 1fr' }}>
                        <div>Fecha</div>
                        <div>Paciente</div>
                        <div>Método</div>
                        <div>Monto</div>
                        <div>Estado</div>
                        <div>Acciones</div>
                    </div>
                    {pagosFiltrados.map((p) => (
                        <div key={p._id} className="tabla-row" style={{ gridTemplateColumns: '1fr 1.5fr 1fr 1fr 1fr 1fr' }}>
                            <div>{formatearFecha(p.createdAt)}</div>
                            <div>
                                {p.paciente ? `${p.paciente.nombre || ''} ${p.paciente.apellido || ''}`.trim() : 'N/A'}
                            </div>
                            <div>
                                {p.metodoPago === 'transferencia' ? '🏦 Transferencia' : '💳 Mercado Pago'}
                            </div>
                            <div className="monto">{formatearMoneda(p.monto)}</div>
                            <div>
                                <span className={`pago-badge pago-${p.estado}`}>
                                    {p.estado === 'approved' && '✅ Aprobado'}
                                    {p.estado === 'en_revision' && '⏳ En Revisión'}
                                    {p.estado === 'pending' && '⏳ Pendiente'}
                                    {p.estado === 'rejected' && '❌ Rechazado'}
                                    {!['approved', 'en_revision', 'pending', 'rejected'].includes(p.estado) && p.estado}
                                </span>
                            </div>
                            <div>
                                {p.estado === 'en_revision' ? (
                                    <button 
                                        className="btn btn-primary" 
                                        style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}
                                        onClick={() => setPagoSeleccionado(p)}
                                    >
                                        Validar
                                    </button>
                                ) : p.metodoPago === 'transferencia' ? (
                                    <button 
                                        className="btn btn-secondary" 
                                        style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}
                                        onClick={() => setPagoSeleccionado(p)}
                                    >
                                        Ver
                                    </button>
                                ) : '-'}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* MODAL PARA VER COMPROBANTE Y VALIDAR */}
            {pagoSeleccionado && (
                <div className="modal-backdrop" onClick={() => setPagoSeleccionado(null)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
                        <div className="modal-header">
                            <h5 className="modal-title">Validar Comprobante de Pago</h5>
                            <button className="btn-close" onClick={() => setPagoSeleccionado(null)}>×</button>
                        </div>
                        <div className="modal-body" style={{ textAlign: 'center' }}>
                            <p><strong>Paciente:</strong> {pagoSeleccionado.paciente?.nombre} {pagoSeleccionado.paciente?.apellido}</p>
                            <p><strong>Monto:</strong> {formatearMoneda(pagoSeleccionado.monto)}</p>
                            
                            <div style={{ margin: '1.5rem 0', background: '#F5EDE3', padding: '1rem', borderRadius: '8px' }}>
                                {pagoSeleccionado.comprobante ? (
                                    <>
                                        <p style={{ marginBottom: '0.5rem' }}>Comprobante adjunto:</p>
                                        <img 
                                            src={`https://tumasaje.ar/${pagoSeleccionado.comprobante}`} 
                                            alt="Comprobante" 
                                            style={{ maxWidth: '100%', maxHeight: '400px', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} 
                                        />
                                    </>
                                ) : (
                                    <p>No se adjuntó imagen del comprobante.</p>
                                )}
                            </div>

                            {pagoSeleccionado.estado === 'en_revision' && (
                                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                                    <button className="btn btn-danger" onClick={() => handleValidar(pagoSeleccionado._id, 'rechazar')}>
                                        Rechazar Pago
                                    </button>
                                    <button className="btn btn-success" onClick={() => handleValidar(pagoSeleccionado._id, 'aprobar')}>
                                        Aprobar y Confirmar Turno
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminPagos;